import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import CountUp from "react-countup";
import { ArrowRight, Sparkles, TrendingUp, Activity, Globe2 } from "lucide-react";
import HeroCanvas from "@/components/HeroCanvas";
import { HERO } from "@/constants/testIds";

export default function Hero() {
  const [live, setLive] = useState({
    campaigns: 247,
    impressions: 12.4,
    revenue: 8420,
  });

  useEffect(() => {
    const id = setInterval(() => {
      setLive((prev) => ({
        campaigns: prev.campaigns + Math.floor(Math.random() * 3 - 1),
        impressions: +(prev.impressions + (Math.random() * 0.4 - 0.15)).toFixed(
          1,
        ),
        revenue: prev.revenue + Math.floor(Math.random() * 80),
      }));
    }, 2500);
    return () => clearInterval(id);
  }, []);

  return (
    <section className="relative min-h-screen pt-32 pb-20 overflow-hidden">
      {/* Subtle decorative background — no full canvas anymore */}
      <div className="aurora-orb bg-[#0047FF] w-[600px] h-[600px] -top-40 -left-40 opacity-60" />
      <div className="aurora-orb bg-[#8A2BE2] w-[500px] h-[500px] top-40 right-0 opacity-30" />
      <div className="aurora-orb bg-[#00F0FF] w-[400px] h-[400px] bottom-0 left-1/3 opacity-20" />

      <div className="container-x relative z-10 grid lg:grid-cols-[1.2fr_1fr] gap-12 items-center">
        <div>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-full glass text-xs uppercase tracking-[0.2em] mb-8"
          >
            <Sparkles className="w-3.5 h-3.5 text-[#00F0FF]" />
            Advanced Ad Technology
          </motion.div>

          <motion.h1
            data-testid={HERO.title}
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.1 }}
            className="font-display text-[clamp(2.6rem,7.5vw,6.5rem)] font-bold mb-6"
          >
            <span className="block">Target.</span>
            <span className="block aurora-text">Engage.</span>
            <span className="block">Excel.</span>
          </motion.h1>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.3 }}
            className="text-lg md:text-xl text-white/65 max-w-xl mb-10 leading-relaxed"
          >
            Powering Your Advertising Success. Enhance ROI with expert ad
            placements. Our service optimizes placements, ensuring prime
            visibility and engagement, driving{" "}
            <span className="text-white">exceptional results</span> for your
            campaigns.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.45 }}
            className="flex flex-wrap items-center gap-4"
          >
            <a
              href="/signup"
              target="_blank"
              rel="noreferrer"
              data-testid={HERO.ctaPrimary}
              className="btn-primary"
            >
              Start Monetizing
              <ArrowRight className="w-4 h-4" />
            </a>
            <a
              href="#services"
              data-testid={HERO.ctaSecondary}
              className="btn-ghost"
            >
              Explore Solutions
            </a>
          </motion.div>

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 1, delay: 0.7 }}
            className="grid grid-cols-3 gap-6 mt-16 max-w-md"
          >
            {[
              { v: 98, suffix: "%", label: "Fill Rate" },
              { v: 2.5, prefix: "$", suffix: "M+", label: "Monthly Revenue" },
              { v: 500, suffix: "+", label: "Publishers" },
            ].map((s) => (
              <div key={s.label}>
                <div className="font-display text-3xl md:text-4xl font-bold">
                  <CountUp
                    end={s.v}
                    decimals={s.v % 1 !== 0 ? 1 : 0}
                    duration={2.2}
                    prefix={s.prefix || ""}
                    suffix={s.suffix || ""}
                  />
                </div>
                <div className="text-xs text-white/50 mt-1">{s.label}</div>
              </div>
            ))}
          </motion.div>
        </div>

        {/* Side panel — globe in a card + live stats */}
        <motion.div
          data-testid={HERO.liveWidget}
          initial={{ opacity: 0, y: 40 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, delay: 0.5 }}
          className="relative float-slow"
        >
          <div className="glass-strong rounded-3xl p-5 relative overflow-hidden">
            {/* Globe canvas — contained inside the card */}
            <div className="relative rounded-2xl overflow-hidden h-[260px] bg-gradient-to-br from-[#020617] via-[#050B26] to-[#0a1142] border border-white/10 mb-5">
              <HeroCanvas />
              <div className="absolute top-3 left-3 inline-flex items-center gap-2 px-2.5 py-1 rounded-full bg-black/40 backdrop-blur-sm border border-white/10">
                <Globe2 className="w-3 h-3 text-[#00F0FF]" />
                <span className="text-[10px] uppercase tracking-wider text-white/75">
                  Global Network
                </span>
              </div>
              <div className="absolute bottom-3 right-3 inline-flex items-center gap-1.5 text-[10px] uppercase tracking-wider text-emerald-400">
                <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" />
                Live
              </div>
            </div>

            {/* Live activity stats */}
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <Activity className="w-4 h-4 text-[#00F0FF]" />
                <span className="text-sm font-medium">Live Activity</span>
              </div>
              <span className="text-[10px] font-mono text-white/45">
                refreshed every 2.5s
              </span>
            </div>

            <div className="space-y-3">
              <LiveStat label="Active Campaigns" value={live.campaigns} color="#00F0FF" />
              <LiveStat
                label="Impressions/min"
                value={`${live.impressions}K`}
                color="#0047FF"
              />
            </div>

            <div className="mt-5 pt-5 border-t border-white/5 grid grid-cols-3 gap-3">
              <SmallMetric label="Growth" value="+24%" />
              <SmallMetric label="CTR" value="3.2%" sub="↑ 0.8%" />
              <SmallMetric label="Fill" value="98%" sub="↑ 2%" />
            </div>
          </div>

          <div className="absolute -inset-3 -z-10 rounded-3xl bg-gradient-to-br from-[#0047FF]/30 to-[#00F0FF]/20 blur-2xl" />
        </motion.div>
      </div>
    </section>
  );
}

function LiveStat({ label, value, color }) {
  return (
    <div className="flex items-center justify-between">
      <span className="text-sm text-white/60">{label}</span>
      <span className="font-mono text-lg font-bold" style={{ color }}>
        {value}
      </span>
    </div>
  );
}

function SmallMetric({ label, value, sub }) {
  return (
    <div>
      <div className="text-[9px] uppercase tracking-wider text-white/40 mb-0.5">
        {label}
      </div>
      <div className="font-display text-lg font-bold">{value}</div>
      {sub && (
        <div className="text-[9px] text-emerald-400 flex items-center gap-1">
          <TrendingUp className="w-2.5 h-2.5" /> {sub}
        </div>
      )}
    </div>
  );
}
